/**
 * MAX POWER — "how much generation can this site actually hold?"
 *
 * The mirror of the sizing panel above it: that one answers "how small can this
 * be and still serve the load", this one sweeps capacity UPWARD until a
 * documented constraint binds, then inverts the winner through the power stack
 * to answer the question a developer actually asks — how big a datacenter fits.
 *
 * USER-TRIGGERED, never a `useMemo`. A sweep is up to 240 rungs × (2–3 exact
 * solves + a parcel test-fit with polygon clipping); running it on every input
 * change would lock the workbench. This copies the deferred-run pattern
 * `GenerationLayoutParameters` already uses: fingerprint effect clears a stale
 * snapshot, a busy flag, a one-frame timeout so the busy state paints before the
 * synchronous sweep, and a requestId + fingerprint guard so a late result from a
 * superseded run can never land.
 *
 * The panel renders the study's OWN honesty signals rather than a bare number:
 * the binding constraint, the bound quality (never "exact"), every ceiling
 * including the indeterminate ones, and the warnings. A max-power figure without
 * its caveats is worse than no figure.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  GenerationImpactScenarioInput,
  GenerationLayoutConfiguration,
  GenerationMaxPowerStudySnapshot,
} from '../../../types/generationImpact';
import type { SiteBoundaryParcel } from '../../../types/siteBoundary';
import { buildGenerationMaxPowerStudySnapshot } from '../../../lib/generationImpact/maxPowerStudy';
import { hashGenerationImpactPayload } from '../../../lib/generationImpact/integrity';
import type { PueSource } from '../../../lib/powerStack';

interface GenerationMaxPowerPanelProps {
  input: GenerationImpactScenarioInput;
  parcels?: readonly SiteBoundaryParcel[];
  layout?: GenerationLayoutConfiguration;
  fallbackAnchor?: { lat: number; lng: number };
  fallbackEnvelopeAcres?: number;
  pue?: number;
  pueSource?: PueSource;
  powerFactor?: number;
  /** Fired with a completed study so the parent can offer the intent stamp. */
  onStudyResolved?: (study: GenerationMaxPowerStudySnapshot | null) => void;
  /** Present only when stamping is currently allowed; the parent owns the rules. */
  onStampToIntent?: () => void;
  /** Why stamping is unavailable, shown instead of the button. */
  stampDisabledReason?: string;
  /**
   * Where the stamp will land, when the parent knows more than "Project
   * Intent" — e.g. a PHASED site, where the figure also becomes the final
   * phase's cumulative capacity. Caption only; the parent owns the rules.
   */
  stampTargetLabel?: string;
}

const r1 = (n: number) => (Math.round(n * 10) / 10).toLocaleString('en-US');

const STATUS_TONE: Record<string, string> = {
  bounded: 'text-text-primary',
  'not-binding': 'text-text-muted',
  indeterminate: 'text-warning',
  'not-applicable': 'text-text-muted',
  'not-scaling': 'text-warning',
};

const BOUND_QUALITY_COPY: Record<string, string> = {
  'bounded-by-documented-constraints':
    'Bounded by the constraints that are documented for this site. The documented set is still incomplete — see the notes below.',
  'upper-bound-undocumented-constraints':
    'This is an UPPER BOUND, not a maximum: at least one constraint is undocumented, so the true ceiling can only be lower.',
  'search-ceiling-reached':
    'No documented constraint bound this scenario within the evaluated range — the search ceiling stopped the sweep. Read the figure as "at least".',
};

export const GenerationMaxPowerPanel: React.FC<GenerationMaxPowerPanelProps> = ({
  input, parcels, layout, fallbackAnchor, fallbackEnvelopeAcres,
  pue, pueSource, powerFactor, onStudyResolved, onStampToIntent, stampDisabledReason,
  stampTargetLabel,
}) => {
  const [busy, setBusy] = useState(false);
  const [snapshot, setSnapshot] = useState<{ fingerprint: string; study: GenerationMaxPowerStudySnapshot } | null>(null);
  const requestRef = useRef(0);
  const timerRef = useRef<number | null>(null);

  // Any change to the scenario or the layout invalidates a computed study —
  // the land ceiling in particular is layout-conditional.
  const fingerprint = useMemo(
    () => `${hashGenerationImpactPayload(input)}:${hashGenerationImpactPayload(layout ?? null)}:${parcels?.length ?? 0}`,
    [input, layout, parcels],
  );
  const latestFingerprintRef = useRef(fingerprint);
  latestFingerprintRef.current = fingerprint;

  useEffect(() => {
    requestRef.current += 1;
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setBusy(false);
    setSnapshot(null);
    onStudyResolved?.(null);
  }, [fingerprint, onStudyResolved]);

  useEffect(() => () => {
    requestRef.current += 1;
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
  }, []);

  const runStudy = useCallback(() => {
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    const requestId = ++requestRef.current;
    const runFingerprint = fingerprint;
    setBusy(true);
    setSnapshot(null);
    // Yield one frame so the busy state paints before the synchronous sweep.
    timerRef.current = window.setTimeout(() => {
      try {
        const study = buildGenerationMaxPowerStudySnapshot(
          { input, parcels, layout, fallbackAnchor, fallbackEnvelopeAcres },
          { pue, pueSource, powerFactor },
        );
        // Guard against a superseded run landing late.
        if (requestRef.current === requestId && latestFingerprintRef.current === runFingerprint) {
          setSnapshot({ fingerprint: runFingerprint, study });
          onStudyResolved?.(study.status === 'available' ? study : null);
        }
      } finally {
        if (requestRef.current === requestId) {
          timerRef.current = null;
          setBusy(false);
        }
      }
    }, 30);
  }, [fingerprint, input, parcels, layout, fallbackAnchor, fallbackEnvelopeAcres,
    pue, pueSource, powerFactor, onStudyResolved]);

  const study = snapshot?.fingerprint === fingerprint ? snapshot.study : null;
  const max = study?.status === 'available' ? study.maximum : null;
  // When nothing bound the sweep, the figure is a floor, not an answer — the
  // headline must SAY so, because a large number reads as authoritative even
  // with a caveat underneath it.
  const atLeast = study?.boundQuality === 'search-ceiling-reached' ? '≥ ' : '';

  return (
    <section
      className="rounded-2xl border border-border bg-card shadow-card"
      aria-labelledby="generation-max-power-heading"
      data-testid="generation-max-power-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">Envelope screen</p>
          <h3 id="generation-max-power-heading" className="mt-1 text-base font-bold text-text-primary">
            Max power — how much can this site hold?
          </h3>
          <p className="mt-1 max-w-3xl text-xs text-text-muted">
            Sweeps capacity upward from the current equipment basis, testing every rung against land, gas,
            water, CO₂ export and receptor noise, and reports the largest fleet where every documented
            constraint passes at once — plus which one binds.
          </p>
        </div>
        <button
          type="button"
          onClick={runStudy}
          disabled={busy}
          data-testid="generation-max-power-run"
          className="shrink-0 rounded-md border border-primary bg-primary px-3 py-1.5 type-meta font-bold text-on-primary transition-colors disabled:opacity-60"
        >
          {busy ? 'Sweeping…' : study ? 'Re-run study' : 'Run max-power study'}
        </button>
      </div>

      <div className="px-4 py-3">
        {!study && !busy && (
          <p className="text-xs text-text-muted" data-testid="generation-max-power-idle">
            Not yet run. The sweep evaluates many exact solver runs and a parcel test-fit per rung, so it is
            deliberately manual rather than live.
          </p>
        )}

        {busy && (
          <p className="text-xs text-text-muted" data-testid="generation-max-power-busy">
            Evaluating capacity rungs…
          </p>
        )}

        {study?.status === 'unavailable' && (
          <p className="text-xs text-warning" data-testid="generation-max-power-unavailable">
            {study.reason}
          </p>
        )}

        {study?.status === 'available' && max && (
          <div className="space-y-3" data-testid="generation-max-power-result">
            {/* Headline — FIRM capacity and the campus it supports. */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-border-subtle bg-state-hover/40 p-3">
                <div className="type-meta font-bold text-text-muted">Maximum generation</div>
                <div className="mt-1 text-lg font-bold tabular-nums text-text-primary" data-testid="generation-max-power-firm">
                  {atLeast}{r1(max.firmNetMW)} MW firm
                </div>
                <div className="mt-0.5 text-[11px] text-text-muted tabular-nums">
                  {max.blockCount} × {r1(max.isoGrossMWPerBlock)} MW · {r1(max.isoGrossNameplateMW)} MW ISO nameplate
                  {' · '}{r1(max.siteGrossMW)} MW site gross
                </div>
                <div className="mt-1 text-[10px] italic text-text-muted">
                  Firm = output surviving the scenario&apos;s N-{study.basis.contingencyOutages} criterion, materially
                  below installed nameplate.
                </div>
              </div>

              <div className="rounded-lg border border-border-subtle bg-state-hover/40 p-3">
                <div className="type-meta font-bold text-text-muted">Campus it supports</div>
                <div className="mt-1 text-lg font-bold tabular-nums text-text-primary" data-testid="generation-max-power-facility">
                  {atLeast}{r1(max.facilityMW)} MW facility
                </div>
                <div className="mt-0.5 text-[11px] text-text-muted tabular-nums">
                  {atLeast}{r1(max.itLoadMW)} MW IT (PUE {max.pue}) · {atLeast}{r1(max.apparentMVA)} MVA
                </div>
                <div className="mt-1 text-[10px] italic text-text-muted">
                  After a {max.tdLossPct}% T&amp;D derate. At the generation terminals (behind-the-meter reading)
                  it is {r1(max.facilityMWAtGenerationTerminals)} MW.
                </div>
              </div>
            </div>

            {/* The binder + how the figure must be read. */}
            <div className="rounded-lg border border-border-subtle p-3">
              <div className="type-meta font-bold text-text-muted">What binds</div>
              <p className="mt-1 text-xs text-text-primary" data-testid="generation-max-power-binder">
                {study.bindingStatement}
              </p>
              {study.boundQuality && (
                <p
                  className={`mt-1.5 text-[11px] ${study.boundQuality === 'bounded-by-documented-constraints' ? 'text-text-muted' : 'text-warning'}`}
                  data-testid="generation-max-power-bound-quality"
                  data-bound-quality={study.boundQuality}
                >
                  {BOUND_QUALITY_COPY[study.boundQuality]}
                </p>
              )}
            </div>

            {/* Every ceiling, including the ones we cannot compute. */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-left text-[11px]" data-testid="generation-max-power-ceilings">
                <thead>
                  <tr className="border-b border-border-subtle type-meta text-text-muted">
                    <th className="py-1 pr-3 font-bold">Constraint</th>
                    <th className="py-1 pr-3 font-bold">Status</th>
                    <th className="py-1 pr-3 font-bold text-right">Ceiling (site gross)</th>
                    <th className="py-1 font-bold">Basis / why not</th>
                  </tr>
                </thead>
                <tbody>
                  {study.ceilings.map((c) => (
                    <tr key={c.id} className="border-b border-border-subtle/60 align-top" data-testid={`generation-max-power-ceiling-${c.id}`}>
                      <td className="py-1.5 pr-3 text-text-primary">{c.label}</td>
                      <td className={`py-1.5 pr-3 type-meta ${STATUS_TONE[c.status] ?? ''}`}>
                        {c.status}
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular-nums text-text-secondary">
                        {c.ceilingSiteGrossMW != null ? `${r1(c.ceilingSiteGrossMW)} MW` : '—'}
                        {c.conservativeCeilingSiteGrossMW != null && (
                          <div className="text-[10px] text-warning">
                            conservative {r1(c.conservativeCeilingSiteGrossMW)} MW
                          </div>
                        )}
                      </td>
                      <td className="py-1.5 text-text-muted">{c.reason ?? c.basis}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {study.landFitNonMonotonic && (
              <p className="text-[11px] text-warning" data-testid="generation-max-power-nonmonotonic">
                The parcel packer is not monotone in capacity — a larger fleet packed where a smaller one did
                not. The ceiling is the largest verified fit on the evaluated ladder, not a supremum.
              </p>
            )}

            <details className="text-[11px]" data-testid="generation-max-power-caveats">
              <summary className="cursor-pointer text-text-muted">
                Assumptions and limits ({study.warnings.length})
              </summary>
              <ul className="mt-1.5 list-disc space-y-1 pl-4 text-text-muted">
                {study.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </details>

            {/* Stamping writes this figure into Project Intent as the project's
                power requirement, so it is deliberately explicit rather than a
                side effect of running the study. */}
            <div className="flex flex-wrap items-center gap-2 border-t border-border-subtle pt-2">
              {onStampToIntent ? (
                <button
                  type="button"
                  onClick={onStampToIntent}
                  data-testid="generation-max-power-stamp"
                  className="rounded-md border border-border px-3 py-1.5 type-meta font-bold text-text-primary transition-colors hover:bg-state-hover"
                >
                  Stamp to Project Intent
                </button>
              ) : (
                <span className="text-[11px] italic text-text-muted" data-testid="generation-max-power-stamp-blocked">
                  {stampDisabledReason ?? 'Stamping is unavailable for this scenario.'}
                </span>
              )}
              <span className="text-[10px] italic text-text-muted">
                {stampTargetLabel ? `${stampTargetLabel} ` : ''}The server recomputes this study before saving — nothing computed in the browser becomes the
                site&apos;s capacity of record.
              </span>
            </div>

            <p className="text-[10px] italic text-text-muted">
              Ladder: {study.basis.evaluatedRungCount} rungs on the {study.basis.axisLabel.toLowerCase()} axis
              {study.basis.ladderIncrementMW != null ? ` at ${study.basis.ladderIncrementMW} MW steps` : ''}
              {' · '}land basis {study.basis.landBasis}
              {' · '}capacity between rungs is unverified.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};
